import type { UpdateInstitution, UpdateOrCreateInstitution } from '~/types'
import { createInstitutionSchema } from '~/server/schemas'

export default (input: InstitutionOpener, backCallback?: () => void) => {
  const { institutionQuery } = useQuery()

  const where = computed(() => useWhere('institution', {
    id: input.mode === 'update' ? input.data.id : undefined,
  }))
  const { data } = institutionQuery.findOne(where.value)

  const payload = computed<UpdateOrCreateInstitution | undefined>((): UpdateOrCreateInstitution | undefined => {
    if (input.mode === 'create') {
      return {
        mode: 'create',
        data: {
          name: '',
          status: 'active',
        },
      }
    }

    if (data.value?.account) {
      return {
        mode: 'update',
        data: {
          ...data.value,
          email: data.value.account.email,
          phoneNumber: data.value.account.mobilePhone,
          landLinePhone: data.value.account.landLinePhone,
          account: {
            id: data.value.account.id,
            role: 'institution',
          },
        },
      } as UpdateInstitution
    }
  })

  const isLoading = ref(false)
  const { $i18n } = useNuxtApp()
  const { $trpc, queryClient, useMutation, makeTrpcErrorToast, makeTrpcSuccessToast } = useMutationHelpers()
  const create = useMutation({
    mutationFn: $trpc.institutions.createInstitution.mutate,
    onError: makeTrpcErrorToast({ entity: $i18n.t('institution.entity'), mode: 'create' }, () => isLoading.value = false),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['institutions', 'getAll'] })
      makeTrpcSuccessToast({ entity: $i18n.t('institution.entity'), mode: 'create' })
      if (backCallback) {
        backCallback()
      }
    },
  })
  const update = useMutation({
    mutationFn: $trpc.institutions.updateInstitution.mutate,
    onError: makeTrpcErrorToast({ entity: $i18n.t('institution.entity'), mode: 'update' }, () => isLoading.value = false),
    onSuccess: async (payload) => {
      await queryClient.invalidateQueries({ queryKey: ['institutions', 'getAll'] })
      await queryClient.invalidateQueries({ queryKey: ['persons', 'getAll'] })
      await queryClient.invalidateQueries({ queryKey: ['institutions', 'getById', { id: payload.id }] })
      await queryClient.invalidateQueries({ queryKey: ['accounts', 'getById', { id: payload.account?.id }] })
      makeTrpcSuccessToast({ entity: $i18n.t('institution.entity'), mode: 'update' })
      if (backCallback) {
        backCallback()
      }
    },
  })

  const saveCreateOrEdit = (input: UpdateOrCreateInstitution) => {
    isLoading.value = true
    input.data = {
      ...input.data,
      email: input.data.email || null,
      phoneNumber: input.data.phoneNumber || null,
      contactPersonEmail: input.data.contactPersonEmail || null,
    }
    if (input.mode === 'create') {
      create.mutate(createInstitutionSchema.parse(input.data))
    } else {
      update.mutate(input.data)
    }
  }

  return {
    data,
    payload,
    isLoading,
    saveCreateOrEdit,
    back: backCallback,
  }
}
